import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

export interface CreateApiClientOptions {
  baseURL: string;
  timeout?: number;
  /**
   * Retorna o bearer token atual (ou null se não autenticado).
   * `forceRefresh` é repassado em retry de 401 — no Firebase, mapeia para
   * `user.getIdToken(true)` (renovação forçada, ignora o cache local).
   */
  getToken: (forceRefresh?: boolean) => Promise<string | null>;
  /** Headers extras por request (ex.: X-Impersonation-Token no web-admin). */
  getExtraHeaders?: () => Record<string, string>;
  /**
   * Chamado quando um 401 SOBREVIVE ao retry com token renovado — ou seja,
   * sessão genuinamente expirada/revogada. Responsável por signOut + redirect.
   * O erro é re-rejeitado em seguida — nunca é engolido.
   */
  onUnauthorized?: () => Promise<void> | void;
  /** Pathname(s) onde o handler de 401 NÃO dispara (evita loop). Default: '/login'. */
  loginPath?: string;
}

interface RetriableConfig extends InternalAxiosRequestConfig {
  /** Marca de retry pós-refresh — garante no máximo UMA repetição por request. */
  _retried401?: boolean;
}

export function createApiClient(options: CreateApiClientOptions): AxiosInstance {
  const { baseURL, timeout = 15_000, getToken, getExtraHeaders, onUnauthorized, loginPath = '/login' } = options;

  const api = axios.create({ baseURL, timeout });

  api.interceptors.request.use(async (config) => {
    if (getExtraHeaders) {
      for (const [key, value] of Object.entries(getExtraHeaders())) {
        config.headers[key] = value;
      }
    }
    const token = await getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Guarda anti-reentrância: vários requests podem falhar com 401 ao mesmo tempo.
  let handling401 = false;

  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error?.response?.status;
      const original = error?.config as RetriableConfig | undefined;

      // 1ª linha de defesa: 401 pode ser token velho (cache/skew) ou request
      // que saiu sem token (boot). Renova com forceRefresh e repete UMA vez
      // antes de considerar a sessão morta.
      if (status === 401 && original && !original._retried401) {
        original._retried401 = true;
        try {
          const fresh = await getToken(true);
          if (fresh) {
            original.headers.Authorization = `Bearer ${fresh}`;
            return await api.request(original);
          }
        } catch {
          // refresh falhou — cai no tratamento de sessão morta abaixo
        }
      }

      // 2ª linha: 401 persistiu após retry (ou não havia como renovar) —
      // sessão genuinamente expirada/revogada.
      if (
        status === 401 &&
        onUnauthorized &&
        !handling401 &&
        !window.location.pathname.startsWith(loginPath)
      ) {
        handling401 = true;
        try {
          await onUnauthorized();
        } catch {
          // signOut/redirect falhou — segue para o reject; próximo 401 tenta de novo
          handling401 = false;
        }
      }
      return Promise.reject(error);
    }
  );

  return api;
}
